import React, { createContext, useEffect, useMemo, useState } from "react";

type NavContextType = {
	/** `null` means the visitor has not chosen an audience yet. */
	currentNavState: string | null;
	setCurrentNavState: (value: string) => void;
};

/** The only audiences the site recognises. */
export const NAV_STATES = ["individual", "business", "academic"];

/**
 * Audience used to populate menus for a visitor who has not chosen one. This is
 * a rendering fallback only — it is never stored, never highlights a nav
 * segment, and never drives commerce links.
 */
export const NAV_STATE_FALLBACK = "business";

/**
 * Versioned key. The previous provider wrote "business" into `navState` on
 * every visit, so that key cannot distinguish a real choice from a default —
 * every existing browser holds one. Reading a new key lets those visitors start
 * genuinely unset; the stale key is cleared on first load.
 */
const NAV_STATE_KEY = "navState.v2";
const LEGACY_NAV_STATE_KEY = "navState";

export const NavContext = createContext<NavContextType>({
	currentNavState: null,
	setCurrentNavState: () => {},
});

function JSONtryParse(value: unknown) {
	try {
		if (typeof value !== "string") return null;
		return JSON.parse(value);
	} catch (error) {
		return null;
	}
}

function getInitialNavState(): string | null {
	if (typeof window === "undefined") return null;

	/** Storage access throws outright in some privacy modes. */
	try {
		window.localStorage.removeItem(LEGACY_NAV_STATE_KEY);

		const parsed = JSONtryParse(window.localStorage.getItem(NAV_STATE_KEY));
		/** Anything we do not recognise is treated as no choice at all. */
		return typeof parsed === "string" && NAV_STATES.includes(parsed)
			? parsed
			: null;
	} catch (error) {
		return null;
	}
}

/**
 * Writes the visitor's audience. Exported so a handler that navigates away in
 * the same tick can persist before the page unloads, rather than relying on an
 * effect that may not flush in time.
 */
export function persistNavState(value: string): void {
	if (typeof window === "undefined") return;
	if (!NAV_STATES.includes(value)) return;

	try {
		window.localStorage.setItem(NAV_STATE_KEY, JSON.stringify(value));
	} catch (error) {
		/** Storage unavailable — the choice still holds for this session. */
	}
}

export const NavProvider: React.FC<React.PropsWithChildren> = ({
	children,
}) => {
	const [currentNavState, setCurrentNavState] = useState<string | null>(
		getInitialNavState
	);

	useEffect(() => {
		/** Never fabricate a choice the visitor has not made. */
		if (currentNavState === null) return;
		persistNavState(currentNavState);
	}, [currentNavState]);

	const value = useMemo(
		() => ({ currentNavState, setCurrentNavState }),
		[currentNavState]
	);

	return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
};
